import {
  PAGE_SIZES,
  type HeaderComponent,
  type PageMode,
  type PageSizeKey,
} from '@rh/shared/schemas/design'
import {
  WATERMARK_ID,
  activeComponentsProp,
  applyDesignToEditor,
  generateComponentId,
  type ActiveTab,
  type DesignEditorState,
} from './design-state'
import type { SavedDesign } from '@rh/shared/schemas/design'
import { initOverlay } from './text-toolbar'

const MAX_HISTORY = 50

interface HistorySnapshot {
  components: HeaderComponent[]
}

interface TabHistory {
  stack: HistorySnapshot[]
  pointer: number
}

/**
 * Undo/redo + component CRUD actions over a DesignEditorState. Each tab
 * (first/subsequent) gets its own independent history stack, matching the
 * sandbox's per-tab undo behavior.
 */
export function createDesignActions(state: DesignEditorState) {
  const history: Record<ActiveTab, TabHistory> = {
    first: {
      stack: [{ components: state.firstPageComponents.value }],
      pointer: 0,
    },
    subsequent: {
      stack: [{ components: state.subsequentPageComponents.value }],
      pointer: 0,
    },
  }

  function activeHistory(): TabHistory {
    return history[state.activeTab.value]
  }

  function pushHistory() {
    const h = activeHistory()
    const snapshot: HistorySnapshot = {
      components: activeComponentsProp(state).value,
    }
    h.stack = h.stack.slice(0, h.pointer + 1)
    h.stack.push(snapshot)
    if (h.stack.length > MAX_HISTORY) h.stack.shift()
    h.pointer = h.stack.length - 1
  }

  function undo() {
    const h = activeHistory()
    if (h.pointer <= 0) return
    h.pointer -= 1
    activeComponentsProp(state).set(h.stack[h.pointer].components)
    state.selectedId.set(null)
    // History steps can swap the component set wholesale (e.g. across a
    // wizard replaceAll) — remount the canvas (see designEpoch).
    state.designEpoch.set(state.designEpoch.value + 1)
  }

  function redo() {
    const h = activeHistory()
    if (h.pointer >= h.stack.length - 1) return
    h.pointer += 1
    activeComponentsProp(state).set(h.stack[h.pointer].components)
    state.selectedId.set(null)
    state.designEpoch.set(state.designEpoch.value + 1)
  }

  function updateComponent(updated: HeaderComponent, commit: boolean) {
    const p = activeComponentsProp(state)
    p.set(p.value.map(c => (c.id === updated.id ? updated : c)))
    if (commit) pushHistory()
  }

  function deleteComponent(id: string) {
    const p = activeComponentsProp(state)
    p.set(p.value.filter(c => c.id !== id))
    if (state.selectedId.value === id) state.selectedId.set(null)
    pushHistory()
  }

  // Index-based z-order move, matching the sandbox's moveZ. The resume
  // block always sits at index 0, so other components treat index 1 as
  // the floor — nothing can be sent behind the resume.
  function moveZ(from: number, to: number) {
    const p = activeComponentsProp(state)
    const hasResume = p.value[0]?.type === 'resume'
    const minIdx = hasResume ? 1 : 0
    const clamped = Math.max(minIdx, to)
    const arr = [...p.value]
    const [item] = arr.splice(from, 1)
    arr.splice(clamped, 0, item)
    p.set(arr)
    pushHistory()
  }

  function visibleCenter(width: number, height: number) {
    const { w, h } = PAGE_SIZES[state.pageSize.value]
    return { x: Math.max(0, (w - width) / 2), y: Math.max(0, (h - height) / 2) }
  }

  function addTextBlock() {
    const pos = visibleCenter(200, 36)
    const c: HeaderComponent = {
      id: generateComponentId('text'),
      type: 'text',
      x: pos.x,
      y: pos.y,
      width: 200,
      height: 36,
      rotation: 0,
      content: 'New Text Block',
      fontSize: 16,
      fontFamily: 'Arial, sans-serif',
      textAlign: 'left',
      lineHeight: 1.25,
      color: '#333333',
    }
    activeComponentsProp(state).set([...activeComponentsProp(state).value, c])
    state.selectedId.set(c.id)
    pushHistory()
    // Open the editing overlay immediately, matching the sandbox.
    requestAnimationFrame(() => initOverlay(c.content))
    return c
  }

  function addContainer() {
    const pos = visibleCenter(200, 80)
    const side = { width: 1, style: 'solid' as const, color: '#6366f1' }
    const c: HeaderComponent = {
      id: generateComponentId('container'),
      type: 'container',
      x: pos.x,
      y: pos.y,
      width: 200,
      height: 80,
      rotation: 0,
      backgroundColor: '#e0e7ff',
      borderLinked: true,
      borderTop: side,
      borderRight: { ...side },
      borderBottom: { ...side },
      borderLeft: { ...side },
      radiusLinked: true,
      radiusTL: 4,
      radiusTR: 4,
      radiusBR: 4,
      radiusBL: 4,
      padding: 8,
    }
    activeComponentsProp(state).set([...activeComponentsProp(state).value, c])
    state.selectedId.set(c.id)
    pushHistory()
  }

  function addImage() {
    const pos = visibleCenter(200, 150)
    const c: HeaderComponent = {
      id: generateComponentId('image'),
      type: 'image',
      x: pos.x,
      y: pos.y,
      width: 200,
      height: 150,
      rotation: 0,
      originalWidth: 0,
      originalHeight: 0,
    }
    activeComponentsProp(state).set([...activeComponentsProp(state).value, c])
    state.selectedId.set(c.id)
    pushHistory()
    return c
  }

  function toggleWatermark() {
    const p = activeComponentsProp(state)
    const exists = p.value.some(c => c.id === WATERMARK_ID)
    if (exists) {
      p.set(p.value.filter(c => c.id !== WATERMARK_ID))
      if (state.selectedId.value === WATERMARK_ID) state.selectedId.set(null)
    } else {
      const { w: pgW, h: pgH } = PAGE_SIZES[state.pageSize.value]
      const watermark: HeaderComponent = {
        id: WATERMARK_ID,
        type: 'watermark',
        x: pgW / 2 - 250,
        y: pgH / 2 - 40,
        width: 500,
        height: 80,
        rotation: -30,
        content: 'CONFIDENTIAL',
        fontSize: 52,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        lineHeight: 1.2,
        color: 'rgba(220,38,38,0.3)',
      }
      p.set([...p.value, watermark])
      state.selectedId.set(WATERMARK_ID)
    }
    pushHistory()
  }

  /**
   * Switches the editing tab. If firstAndSubsequent mode was just entered,
   * the first visit to the subsequent tab re-seeds it from the current
   * first-page design (with a fresh history), matching the sandbox — so the
   * user starts from what they've built, not from stale stored components.
   */
  function switchTab(tab: ActiveTab) {
    if (tab === state.activeTab.value) return
    if (tab === 'subsequent' && state.subsequentSeedPending.value) {
      const copy = JSON.parse(
        JSON.stringify(state.firstPageComponents.value)
      ) as HeaderComponent[]
      state.subsequentPageComponents.set(copy)
      history.subsequent = { stack: [{ components: copy }], pointer: 0 }
      state.subsequentSeedPending.set(false)
    }
    state.selectedId.set(null)
    state.activeTab.set(tab)
  }

  /**
   * Changes how the design applies across pages. The first page is always
   * authoritative: leaving firstAndSubsequent returns to the first tab (so
   * 'all'/'firstOnly' never adopt the subsequent tab's design), and
   * entering it arms the seed flag so the subsequent tab starts as a copy
   * of the first page.
   */
  function setPageMode(mode: PageMode) {
    if (mode === state.pageMode.value) return
    if (
      state.pageMode.value === 'firstAndSubsequent' &&
      state.activeTab.value === 'subsequent'
    ) {
      switchTab('first')
    }
    if (mode === 'firstAndSubsequent') {
      state.subsequentSeedPending.set(true)
    }
    state.pageMode.set(mode)
  }

  function setPageSize(size: PageSizeKey) {
    state.pageSize.set(size)
  }

  /**
   * Replaces the whole design (setup wizard hand-off). Both tab histories
   * record the new state so the pre-wizard design is one Undo away —
   * pageMode/pageSize aren't in history, but the components (the actual
   * work) are what Undo needs to bring back.
   */
  function replaceAll(design: SavedDesign) {
    applyDesignToEditor(state, design)
    const tabs: Array<[ActiveTab, HeaderComponent[]]> = [
      ['first', design.firstPage.components],
      ['subsequent', design.subsequentPages.components],
    ]
    for (const [tab, components] of tabs) {
      const h = history[tab]
      h.stack = h.stack.slice(0, h.pointer + 1)
      h.stack.push({ components })
      if (h.stack.length > MAX_HISTORY) h.stack.shift()
      h.pointer = h.stack.length - 1
    }
  }

  return {
    undo,
    redo,
    pushHistory,
    updateComponent,
    deleteComponent,
    moveZ,
    addTextBlock,
    addContainer,
    addImage,
    toggleWatermark,
    switchTab,
    setPageMode,
    setPageSize,
    replaceAll,
  }
}

export type DesignActions = ReturnType<typeof createDesignActions>
